import { ref, computed, readonly, onMounted, onBeforeUnmount } from 'vue'
import axios from '@/services/axios'
import { useStoreDetection } from './useStoreDetection'

/**
 * CompassAI status data structure
 * @typedef {Object} CompassStatus
 * @property {string} StoreNumber
 * @property {boolean} CompassAIEnabled
 * @property {boolean} HasSufficientData
 * @property {number} DaysOfData
 * @property {number} WeeksOfData
 * @property {number} ProductsWithBaseline
 * @property {number} TotalProducts
 * @property {number} BaselineCoverage
 * @property {string|null} OldestDataDate
 * @property {string|null} LastPlanDate
 * @property {string|null} LastPlanStatus
 * @property {string} DataStatus - DISABLED, NO_DATA, INSUFFICIENT, LIMITED, READY
 * @property {string} Message
 */

/**
 * Singleton state for caching CompassAI status across components
 * This prevents redundant API calls when multiple components use this composable
 */
const statusCache = ref(null)
const cacheStoreId = ref(null)
const isLoading = ref(false)
const fetchError = ref(null)
const lastFetchTime = ref(null)

/**
 * Cache duration in milliseconds (5 minutes)
 */
const CACHE_DURATION = 5 * 60 * 1000

/**
 * API request timeout in milliseconds
 */
const REQUEST_TIMEOUT = 45000
const COMPASS_STATUS_API_ENABLED = false

/**
 * Composable for managing CompassAI status across the application
 * Provides caching, automatic store change detection, and navigation filtering
 */
export function useCompassStatus() {
  const { currentStoreId, detectStoreId } = useStoreDetection()

  // Track abort controller for cancellation
  let abortController = null

  /**
   * Whether the cache is still valid
   */
  const isCacheValid = computed(() => {
    if (!statusCache.value || !lastFetchTime.value) return false
    if (cacheStoreId.value !== currentStoreId.value) return false
    
    return (Date.now() - lastFetchTime.value) < CACHE_DURATION
  })

  /**
   * Whether CompassAI is enabled for the current store
   */
  const isEnabled = computed(() => {
    return statusCache.value?.CompassAIEnabled === true
  })

  /**
   * Whether the store has sufficient data for forecasting
   */
  const hasSufficientData = computed(() => {
    return statusCache.value?.HasSufficientData === true
  })

  /**
   * Current data status (DISABLED, NO_DATA, INSUFFICIENT, LIMITED, READY)
   * Returns null if status hasn't been fetched yet
   */
  const dataStatus = computed(() => {
    if (!statusCache.value) return null
    
    return statusCache.value.DataStatus || null
  })

  /**
   * Human-readable status message
   */
  const statusMessage = computed(() => {
    return statusCache.value?.Message || ''
  })

  /**
   * Number of weeks of historical data available
   */
  const weeksOfData = computed(() => {
    return statusCache.value?.WeeksOfData || 0
  })

  /**
   * Baseline coverage percentage (0-100)
   */
  const baselineCoverage = computed(() => {
    return statusCache.value?.BaselineCoverage || 0
  })

  /**
   * Full status object (readonly)
   */
  const status = computed(() => {
    return statusCache.value
  })

  /**
   * Cancel any pending requests
   */
  function cancelPendingRequests() {
    if (abortController) {
      abortController.abort()
      abortController = null
    }
  }

  /**
   * Fetch the CompassAI status from the API
   * @param {boolean} forceRefresh - Force refresh even if cache is valid
   * @returns {Promise<CompassStatus|null>}
   */
  async function fetchStatus(forceRefresh = false, requestTimeout = REQUEST_TIMEOUT) {
    if (!COMPASS_STATUS_API_ENABLED) {
      return statusCache.value
    }

    // Use cache if valid and not forcing refresh
    if (!forceRefresh && isCacheValid.value) {
      return statusCache.value
    }

    // Detect store ID if not already set
    const storeId = currentStoreId.value || await detectStoreId()
    if (!storeId) {
      fetchError.value = 'No store selected'
      
      return null
    }

    // Cancel any pending requests
    cancelPendingRequests()

    isLoading.value = true
    fetchError.value = null
    abortController = new AbortController()

    try {
      const response = await axios.get('/api/compassai/status', {
        params: { storeNumber: storeId },
        timeout: requestTimeout,
        signal: abortController.signal,
      })

      // Handle error response
      if (response.data?.error) {
        fetchError.value = response.data.error

        // Still cache the response for navigation filtering
        statusCache.value = response.data
        cacheStoreId.value = storeId
        lastFetchTime.value = Date.now()
        
        return response.data
      }

      // Cache the successful response
      statusCache.value = response.data
      cacheStoreId.value = storeId
      lastFetchTime.value = Date.now()
      fetchError.value = null

      return response.data
    } catch (err) {
      // Don't treat abort as an error
      if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
        return statusCache.value
      }

      // Handle timeout
      if (err.code === 'ECONNABORTED') {
        fetchError.value = 'Uh-oh — I hit a snag pulling that data. Give me a second and try again.'
        
        return null
      }

      // Handle other errors
      fetchError.value = err.response?.data?.error || err.message || 'Failed to check CompassAI status'

      // Set a disabled status on error to prevent navigation issues
      statusCache.value = {
        CompassAIEnabled: false,
        HasSufficientData: false,
        DataStatus: 'DISABLED',
        Message: fetchError.value,
      }
      cacheStoreId.value = storeId
      lastFetchTime.value = Date.now()

      return null
    } finally {
      isLoading.value = false
      abortController = null
    }
  }

  /**
   * Clear the cached status (useful when logging out or switching accounts)
   */
  function clearCache() {
    statusCache.value = null
    cacheStoreId.value = null
    lastFetchTime.value = null
    fetchError.value = null
  }

  /**
   * Handle store change event - refresh status for new store
   */
  function handleStoreChange() {
    // Clear cache since store changed
    clearCache()

    // Fetch new status (non-blocking)
    fetchStatus(true).catch(() => {
      // Silently handle errors - status will show as disabled
    })
  }

  /**
   * Check if CompassAI pages should be shown in navigation
   * @returns {boolean}
   */
  function shouldShowInNavigation() {
    // If we haven't fetched status yet, don't hide navigation
    if (!statusCache.value) return true

    // Hide only if explicitly disabled
    return statusCache.value.CompassAIEnabled !== false
  }

  /**
   * Get the alert type for status banners based on DataStatus
   * @returns {'error'|'warning'|'info'|'success'}
   */
  function getAlertType() {
    switch (dataStatus.value) {
    case 'DISABLED':
      return 'error'
    case 'NO_DATA':
      return 'warning'
    case 'INSUFFICIENT':
      return 'warning'
    case 'LIMITED':
      return 'info'
    case 'READY':
      return 'success'
    default:
      return 'info'
    }
  }

  /**
   * Get the i18n key for status messages
   * @returns {string}
   */
  function getStatusMessageKey() {
    const statusKey = dataStatus.value?.toLowerCase() || 'disabled'
    
    return `compassai.status.${statusKey}`
  }

  /**
   * Whether features requiring forecasts should be disabled
   */
  const shouldDisableForecasts = computed(() => {
    return !hasSufficientData.value
  })

  /**
   * Initialize the composable and set up event listeners
   */
  function setupListeners() {
    window.addEventListener('store-changed', handleStoreChange)

    // Return cleanup function
    return () => {
      window.removeEventListener('store-changed', handleStoreChange)
      cancelPendingRequests()
    }
  }

  return {
    // State
    status: readonly(status),
    isLoading: readonly(isLoading),
    error: readonly(fetchError),

    // Computed properties
    isEnabled,
    hasSufficientData,
    dataStatus,
    statusMessage,
    weeksOfData,
    baselineCoverage,
    shouldDisableForecasts,

    // Methods
    fetchStatus,
    clearCache,
    cancelPendingRequests,
    shouldShowInNavigation,
    getAlertType,
    getStatusMessageKey,
    setupListeners,
  }
}

/**
 * Helper to get status for navigation filtering (synchronous check)
 * This is useful for navigation components that can't use async
 */
export function getCompassStatusSync() {
  // If cache is empty, return null for isEnabled so navigation shows by default
  if (!statusCache.value) {
    return {
      isEnabled: null, // null = unknown, show by default
      hasSufficientData: null,
      dataStatus: null,
    }
  }

  return {
    isEnabled: statusCache.value.CompassAIEnabled === true,
    hasSufficientData: statusCache.value.HasSufficientData === true,
    dataStatus: statusCache.value.DataStatus || 'DISABLED',
  }
}

/**
 * Prefetch CompassAI status (call this on app init)
 * @returns {Promise<CompassStatus|null>}
 */
export async function prefetchCompassStatus() {
  const { fetchStatus } = useCompassStatus()
  
  return fetchStatus(true)
}
